import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Text } from '@chakra-ui/react';
import { fetchData } from '../../services/fetch';
import { flushAllPending } from '../../hooks/useDebouncedSave';
import Loader from '../../components/Feedback/Loader';
import { useSSO } from '../../context/SSO';
import { AuditModeProvider, useAuditMode } from '../../context/AuditMode';
import { EditStatusModal } from '../../components/Modals';
import AuditHeader from './AuditHeader';
import { AUDIT } from '../../theme/auditTokens';
import Questionnaire from '../questionnaires/Questionnaire';
import PeriodicType from '../questionnaires/PeriodicType';
import DiagnosisType from '../questionnaires/DiagnosisType';
import PillarCertification from '../questionnaires/PillarCertification';
import SimpleCertification from '../questionnaires/SimpleCertification';

// Status em que a auditoria está encerrada ou é a vez da empresa: o auditor não
// deve mexer. MASTER ignora status (EG-54) — inclusive certificados sem
// certificate_status_control (legado pré-EG-22, caem em 'não enviado').
const CLOSED_STATUSES = ['auditado', 'avaliado'];
const COMPANY_TURN_STATUSES = ['em ajustes', 'aguardando adequações', 'não enviado'];

// O header lê `headerData` do contexto (publicado pelo renderer) — por isso
// vive DENTRO do AuditModeProvider, num componente próprio.
function AuditShell({ identity, renderContent, editStatus }) {
 const auditMode = useAuditMode();
 return (
  <>
   <AuditHeader {...identity} headerData={auditMode?.headerData} />
   {renderContent()}
   {editStatus}
  </>
 );
}

export default function AuditView() {
 const [params] = useSearchParams();
 const navigate = useNavigate();
 const { user } = useSSO();

 const roles = user?.user?.roles || [];
 const isMaster = roles.some((role) => role.name === 'MASTER');

 const surveyId = params.get('survey');
 const companyId = params.get('company');
 const type = params.get('type');
 const kind = params.get('kind');
 const hasPillar = params.get('pillar') === 'true';
 // Id do certificate_status_control OU diagnostic_status_control (nunca os
 // dois — a lista já resolve qual dos dois se aplica antes de montar o link).
 const controlId = params.get('control');
 const name = params.get('name') || 'Auditoria';
 const statusParam = (params.get('status') || '').toLowerCase();

 const [leaving, setLeaving] = useState(false);
 const [statusModalOpen, setStatusModalOpen] = useState(false);

 // Nota/comentário pendente (debounce de ~1s) tem que terminar de salvar antes
 // de sair da tela — clicar em "Voltar" logo após editar não pode descartar o
 // que ainda não foi persistido.
 async function handleBack() {
  setLeaving(true);
  await flushAllPending();
  navigate('/audit');
 }

 const { data: companyData, isLoading } = useSWR(
  companyId ? `/company/${companyId}` : null,
  fetchData
 );

 const isCertification = kind === 'certificate' || type === 'certification';
 const isDiagnostic = !isCertification && type === 'diagnostic';

 // Status ao vivo (não o snapshot que a lista tinha quando o link foi montado) —
 // permite reabrir a mesma aba depois de editar o status e ver o valor atual, e
 // dá o `mutate` que o modal de status usa pra atualizar o header ao salvar.
 const { data: certificateControl, mutate: mutateCertificateControl } = useSWR(
  isCertification && controlId ? `certificate-status-control/${controlId}` : null,
  fetchData
 );
 const { data: diagnosticControl, mutate: mutateDiagnosticControl } = useSWR(
  isDiagnostic && surveyId && companyId
   ? `diagnostic-status-control/${surveyId}/company/${companyId}`
   : null,
  fetchData
 );

 const liveStatus = isCertification ? certificateControl?.status : diagnosticControl?.status;
 const currentStatus = (liveStatus || statusParam || '').toLowerCase();
 const hasStatusControl = (isCertification || isDiagnostic) && !!controlId;

 async function refreshStatus() {
  if (isCertification) await mutateCertificateControl();
  else if (isDiagnostic) await mutateDiagnosticControl();
 }

 const isClosed = CLOSED_STATUSES.includes(currentStatus);
 const isCompanyTurn = COMPANY_TURN_STATUSES.includes(currentStatus);

 // Nota: bloqueada quando o fluxo está com a empresa ou encerrado — exceto para
 // o MASTER, que corrige/atribui nota independente do status (EG-54: certificados
 // de antes do fluxo de auditoria nunca têm certificate_status_control, então
 // caem permanentemente em 'não enviado' e travariam para sempre sem este bypass).
 const isScoreDisabled = isCertification ? (isCompanyTurn || isClosed) && !isMaster : false;

 const auditMode = useMemo(
  () => ({ canScore: true, isScoreDisabled }),
  [isScoreDisabled]
 );

 const commonProps = {
  companyId,
  surveyId: surveyId ? Number(surveyId) : undefined,
  companyGroupId: companyData?.idCompanyGroup,
  formName: name,
  selectedCompanyId: companyId,
  isResponsibleUser: false,
  openChat: null,
 };

 function renderContent() {
  if (isCertification) {
   return hasPillar ? (
    <PillarCertification {...commonProps} />
   ) : (
    <SimpleCertification {...commonProps} />
   );
  }
  if (type === 'periodic') return <PeriodicType {...commonProps} />;
  if (type === 'diagnostic') return <DiagnosisType {...commonProps} />;
  return <Questionnaire {...commonProps} />;
 }

 if (isLoading) return <Loader />;

 if (!surveyId || !companyId) {
  return (
   <Container maxW='1280px' py='32px'>
    <Text>Auditoria não encontrada. Volte à lista e abra novamente.</Text>
   </Container>
  );
 }

 const identity = {
  name,
  type,
  companyName: companyData?.name || `Empresa ${companyId}`,
  currentStatus,
  hasStatusControl,
  onBack: handleBack,
  backLoading: leaving,
  onEditStatus: () => setStatusModalOpen(true),
  showMasterClosedNote: isMaster && isClosed,
  showCompanyTurnNote: isCompanyTurn,
 };

 const editStatus = hasStatusControl ? (
  <EditStatusModal
   isOpen={statusModalOpen}
   onClose={() => setStatusModalOpen(false)}
   kind={isCertification ? 'certificate' : 'diagnostic'}
   certificateStatusControlId={isCertification ? controlId : undefined}
   diagnosticStatusControlId={isDiagnostic ? controlId : undefined}
   currentStatus={currentStatus}
   control={certificateControl}
   onStatusUpdate={refreshStatus}
  />
 ) : null;

 return (
  <Box bg={AUDIT.ground} borderRadius='14px' p={['12px', '16px']}>
   <Container maxW='1180px' p={0}>
    <AuditModeProvider value={auditMode}>
     <AuditShell identity={identity} renderContent={renderContent} editStatus={editStatus} />
    </AuditModeProvider>
   </Container>
  </Box>
 );
}
